import { Button } from "@/components/ui/button";
import { Minus, Plus, User, Baby } from "lucide-react";

export default function PassengerSelector({ passengers, onChange }) {
  const updatePassenger = (type, delta) => {
    const newValue = Math.max(type === "adults" ? 1 : 0, Math.min(9, passengers[type] + delta));
    onChange({ ...passengers, [type]: newValue });
  };

  const totalPassengers = passengers.adults + passengers.children + passengers.infants;

  return (
    <div className="space-y-3">
      {/* Summary Badge */}
      <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full w-fit">
        <User className="w-4 h-4 text-purple-600" />
        <span className="text-sm font-medium text-purple-700">
          {totalPassengers} {totalPassengers === 1 ? "Traveller" : "Travellers"}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {/* Adults */}
        <div className="bg-gradient-to-br from-purple-50 to-white p-4 rounded-xl border border-purple-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-purple-900">Adults</p>
              <p className="text-xs text-purple-400">12+ years</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => updatePassenger("adults", -1)}
              disabled={passengers.adults <= 1}
              className="h-9 w-9 rounded-full border-purple-200 hover:bg-purple-50 hover:border-purple-300"
              data-testid="adults-minus-btn"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-2xl font-bold text-purple-900 w-8 text-center" data-testid="adults-count">
              {passengers.adults}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => updatePassenger("adults", 1)}
              disabled={passengers.adults >= 9}
              className="h-9 w-9 rounded-full border-purple-200 hover:bg-purple-50 hover:border-purple-300"
              data-testid="adults-plus-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Children */}
        <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl border border-blue-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-blue-900">Children</p>
              <p className="text-xs text-blue-400">2-11 years</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => updatePassenger("children", -1)}
              disabled={passengers.children <= 0}
              className="h-9 w-9 rounded-full border-blue-200 hover:bg-blue-50 hover:border-blue-300"
              data-testid="children-minus-btn"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-2xl font-bold text-blue-900 w-8 text-center" data-testid="children-count">
              {passengers.children}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => updatePassenger("children", 1)}
              disabled={passengers.children >= 9}
              className="h-9 w-9 rounded-full border-blue-200 hover:bg-blue-50 hover:border-blue-300"
              data-testid="children-plus-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Infants */}
        <div className="bg-gradient-to-br from-pink-50 to-white p-4 rounded-xl border border-pink-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center">
              <Baby className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-pink-900">Infants</p>
              <p className="text-xs text-pink-400">Under 2</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => updatePassenger("infants", -1)}
              disabled={passengers.infants <= 0}
              className="h-9 w-9 rounded-full border-pink-200 hover:bg-pink-50 hover:border-pink-300"
              data-testid="infants-minus-btn"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-2xl font-bold text-pink-900 w-8 text-center" data-testid="infants-count">
              {passengers.infants}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => updatePassenger("infants", 1)}
              disabled={passengers.infants >= passengers.adults}
              className="h-9 w-9 rounded-full border-pink-200 hover:bg-pink-50 hover:border-pink-300"
              data-testid="infants-plus-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {passengers.infants > 0 && passengers.infants >= passengers.adults && (
            <p className="text-[10px] text-pink-500 mt-2 text-center">Max 1 infant per adult</p>
          )}
        </div>
      </div>
    </div>
  );
}
